import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const RoleSelectionPage = () => {
  const [selectedRole, setSelectedRole] = useState('');
  const [loading, setLoading] = useState(false);
  const { currentUser, updateUser } = useAuth();
  const navigate = useNavigate();

  const handleRoleSelection = async (role) => {
    setLoading(true);

    // Update user with selected role
    updateUser({ role });

    // Navigate to appropriate profile setup page
    if (role === 'jobseeker') {
      navigate('/profile/jobseeker');
    } else if (role === 'employer') {
      navigate('/profile/employer');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Welcome to JobMatch! 👋
          </h1>
          <p className="text-xl text-gray-600">
            Hello, <span className="font-semibold text-blue-600">{currentUser?.displayName || currentUser?.email}</span>
          </p>
          <p className="text-gray-600 mt-2">
            Please select your role to continue
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Job Seeker Card */}
          <div
            onClick={() => !loading && setSelectedRole('jobseeker')}
            className={`bg-white rounded-xl shadow-lg p-8 cursor-pointer transition-all transform hover:scale-105 hover:shadow-2xl ${
              selectedRole === 'jobseeker'
                ? 'ring-4 ring-blue-500 border-2 border-blue-500'
                : 'border-2 border-gray-200'
            }`}
          >
            <div className="text-center">
              <div className="text-6xl mb-4">💼</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">
                Job Seeker
              </h2>
              <p className="text-gray-600 mb-6">
                Looking for your next opportunity? Find jobs that match your skills and experience.
              </p>
              <ul className="text-left text-gray-700 space-y-2 mb-6">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Browse job listings</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Get AI-powered job matches</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Build your professional profile</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Track your applications</span>
                </li>
              </ul>
              {selectedRole === 'jobseeker' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRoleSelection('jobseeker');
                  }}
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Processing...' : 'Continue as Job Seeker'}
                </button>
              )}
            </div>
          </div>

          {/* Employer Card */}
          <div
            onClick={() => !loading && setSelectedRole('employer')}
            className={`bg-white rounded-xl shadow-lg p-8 cursor-pointer transition-all transform hover:scale-105 hover:shadow-2xl ${
              selectedRole === 'employer'
                ? 'ring-4 ring-purple-500 border-2 border-purple-500'
                : 'border-2 border-gray-200'
            }`}
          >
            <div className="text-center">
              <div className="text-6xl mb-4">🏢</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">
                Employer
              </h2>
              <p className="text-gray-600 mb-6">
                Hiring talent for your company? Post jobs and find the perfect candidates.
              </p>
              <ul className="text-left text-gray-700 space-y-2 mb-6">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Post job listings</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>AI-powered candidate matching</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Manage applications</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Build company profile</span>
                </li>
              </ul>
              {selectedRole === 'employer' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRoleSelection('employer');
                  }}
                  disabled={loading}
                  className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Processing...' : 'Continue as Employer'}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">
            You can change your role later in settings
          </p>
        </div>
      </div>
    </div>
  );
};

export default RoleSelectionPage;
